import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { AuthModal } from "@core/components/modals/AuthModal";
import { GUEST_ACCOUNT_NUDGE_RESPONSES } from "@core/constants/quotas";
import { setPendingPollClaim } from "@core/lib/pendingPollClaim";

/** Cible tactile d'au moins 44 px sur mobile (WCAG 2.5.5) ; taille normale dès `sm`. */
const TAP_TARGET = "min-h-11 sm:min-h-9";

/**
 * Vrai si l'organisateur SANS COMPTE doit voir l'invitation : au-delà de
 * GUEST_ACCOUNT_NUDGE_RESPONSES réponses, et jamais pour un utilisateur
 * connecté (`claimToken` non nul : il garde « Rattacher ce sondage »).
 * Partagé par Results (qui l'affiche) et GuestOwnerPanel (qui retire alors
 * son encart « Créer un compte », pour n'avoir qu'un seul appel).
 */
export function shouldShowGuestAccountNudge(
  responseCount: number,
  claimToken: string | null | undefined,
): boolean {
  return !claimToken && responseCount > GUEST_ACCOUNT_NUDGE_RESPONSES;
}

export interface GuestAccountNudgeProps {
  poll: { id: string; slug: string };
  /** Nombre de réponses compté côté serveur (`get_poll_results_access`). */
  responseCount: number;
}

/**
 * Invitation à créer un compte gratuit pour l'organisateur sans compte d'un
 * sondage qui a dépassé 20 réponses. Remplace l'ancien plafond dur de 20
 * participants (retiré le 2026-09-30) : rien n'est bloqué ni masqué.
 * Affichée en haut de la page de résultats, sous le compteur de votants.
 * Les deux boutons posent l'intention de rattacher CE sondage, consommée par
 * AuthContext après SIGNED_IN.
 */
export function GuestAccountNudge({
  poll,
  responseCount,
}: GuestAccountNudgeProps) {
  const { t } = useTranslation(["polls"]);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");

  const openAuth = (mode: "signup" | "signin") => {
    setPendingPollClaim(poll.id, poll.slug);
    try {
      localStorage.setItem(
        "auth_return_to",
        `/poll/${encodeURIComponent(poll.slug)}/results`,
      );
    } catch {
      // Stockage indisponible : l'utilisateur reviendra à la main.
    }
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <div
      className="rounded-lg border border-primary/40 bg-primary/10 p-4 space-y-3 min-w-0 text-left"
      data-testid="guest-admin-account-nudge"
    >
      <div className="space-y-1 min-w-0">
        <h3 className="text-base font-semibold text-foreground break-words">
          {t("polls:guest_admin.nudge_title", { count: responseCount })}
        </h3>
        <p className="text-sm text-foreground break-words">
          {t("polls:guest_admin.nudge_body")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button
          type="button"
          className={`shrink-0 ${TAP_TARGET}`}
          onClick={() => openAuth("signup")}
          data-testid="guest-admin-account-nudge-signup"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          {t("polls:guest_admin.nudge_cta")}
        </Button>
        <Button
          type="button"
          variant="link"
          className={`px-0 ${TAP_TARGET}`}
          onClick={() => openAuth("signin")}
          data-testid="guest-admin-account-nudge-signin"
        >
          {t("polls:guest_admin.nudge_signin")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground break-words">
        {t("polls:guest_admin.nudge_note")}
      </p>
      <AuthModal
        key={authMode}
        open={authOpen}
        onOpenChange={setAuthOpen}
        defaultMode={authMode}
      />
    </div>
  );
}
